import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Avatar

} from '@mui/material';
import {
  Article,
  Work,
  People,
  PhotoLibrary,
  Email, // ✅ New icon for Inbox
  ArrowForward
} from '@mui/icons-material';

// --- Configuration for Modern Cards ---
const QuickLinkCard = ({ to, icon, title, subtitle, color }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Card
      component={Link}
      to={to}
      sx={{
        textDecoration: 'none',
        height: '100%',
        borderRadius: 3,
        border: '1px solid #f1f5f9', // Very light border
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          borderColor: 'transparent',
          '& .icon-avatar': {
             bgcolor: color,
             color: '#fff'
          }
        }
      }}
    >
      <CardActionArea sx={{ height: '100%', p: 2 }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 0 }}>
          {/* Icon Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Avatar
              className="icon-avatar"
              sx={{
                bgcolor: `${color}15`, // 15 adds transparency (~8% opacity)
                color: color,
                width: 56,
                height: 56,
                borderRadius: 2, // Rounded square look
                transition: 'all 0.3s ease'
              }}
            >
              {icon}
            </Avatar>
            <ArrowForward sx={{ color: 'text.disabled' }} />
          </Box>
          
          {/* Text Content */}
          <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {subtitle}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  </Grid>
);

export default function AdminHome() {
  const [adminName, setAdminName] = useState('');

  // Get User Name
  useEffect(() => {
    try {
      const user = localStorage.getItem('user');
      if (user) {
        setAdminName(JSON.parse(user).name);
      }
    } catch (err) {
      console.error("Failed to parse user from localStorage", err);
    }
  }, []);

  return (
    <Box sx={{ p: { xs: 0, md: 2 } }}>
      
      {/* 1. Modern Hero Section */}
      <Paper
        sx={{
          p: { xs: 4, md: 6 },
          mb: 5,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', // Dark modern gradient
          color: '#ffffff',
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
        }}
        elevation={0}
      >
        {/* Decorative background circle */}
        <Box sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.03)',
            zIndex: 0,
            pointerEvents: 'none'
        }} />
        
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 1, letterSpacing: '-0.02em' }}>
            {/* ✅ Dynamically shows the name */}
            Welcome back, Admin !
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 400, maxWidth: '600px', fontSize: '1.1rem' }}>
            Here is your command center. Manage your content, track applicants, and respond to inquiries all in one place.
          </Typography>
        </Box>
      </Paper>

      {/* 2. Communication & Jobs Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#64748b', mb: 3, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.1em' }}>
            Recruitment & Inquiries
        </Typography>

        <Grid container spacing={3}>
            {/* ✅ INBOX CARD */}
            <QuickLinkCard
                to="/admin/messages"
                icon={<Email />}
                title="Inbox"
                subtitle="View and respond to contact form inquiries from Horizon Energies & Hauliers."
                color="#ef4444" // Red
            />
            {/* JOB LISTINGS */}
            <QuickLinkCard
                to="/admin/careers"
                icon={<Work />}
                title="Job Listings"
                subtitle="Post new openings, edit requirements, and manage job descriptions."
                color="#f59e0b" // Amber
            />
            {/* APPLICANTS */}
            <QuickLinkCard
                to="/admin/applicants"
                icon={<People />}
                title="Applicants"
                subtitle="Review resumes and manage incoming job applications."
                color="#10b981" // Emerald
            />
        </Grid>
      </Box>

      {/* 3. Content Management Section */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#64748b', mb: 3, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.1em' }}>
            Content Management
        </Typography>
        
        <Grid container spacing={3}>
            {/* BLOGS */}
            <QuickLinkCard
                to="/admin/dashboard"
                icon={<Article />}
                title="Blog Posts"
                subtitle="Create, edit, and publish news and articles to your site."
                color="#3b82f6" // Blue
            />
            {/* GALLERY */}
            <QuickLinkCard
                to="/admin/gallery"
                icon={<PhotoLibrary />}
                title="Media Gallery"
                subtitle="Upload images and organize your visual assets."
                color="#8b5cf6" // Purple
            />
        </Grid>
      </Box>

    </Box>
  );
}